import { useMemo, useState } from 'react';
import type { PrayerTimings } from '../lib/types';
import {
  computeDerivedTimes,
  formatClock,
  formatRange,
  minToHH,
  toMin,
  MADHABS,
  PH_MADHAB_LABELS,
  PH_WINDOWS_BY_MADHAB,
  PH_WINDOWS_I18N,
  type FardBuffers,
  type Madhab,
  type PrayerWindow
} from '../lib/prayerWindows';
import { useLocale } from '../lib/i18n';

const WM_KEY = 'ap_windows_madhab_v1';

function readWindowsMadhab(): Madhab {
  try {
    const raw = localStorage.getItem(WM_KEY) as Madhab | null;
    if (raw && MADHABS.includes(raw)) return raw;
  } catch {
    /* ignore */
  }
  return 'hanafi';
}

/** Resolve a window's clock span from the day's actual derived times (an app improvement over the widget's static times). */
function resolveSpan(
  key: string,
  madhab: Madhab,
  d: ReturnType<typeof computeDerivedTimes>
): { from: string | null; to: string | null } {
  switch (key) {
    case 'afterFajr': return { from: d.fajrFardDone, to: d.sunrise };
    case 'afterFajrAdhan': return { from: d.prayers[0].time, to: d.sunrise };
    case 'sunrise': return { from: d.sunrise, to: minToHH(toMin(d.ishraq)) };
    case 'ishraq': return { from: d.ishraq, to: null };
    case 'zawal': return { from: minToHH(d.zawalStart), to: minToHH(d.zawalEnd) };
    case 'afterAsr': return { from: d.asrFardDone, to: madhab === 'hanbali' ? d.sunset : d.sunsetBegins };
    case 'sunset': return { from: d.sunsetBegins, to: d.sunset };
    case 'yellowSun': return { from: d.sunsetBegins, to: d.sunset };
    default: return { from: null, to: null }; // malikirule / malikirule2 (notes)
  }
}

const ExceptionIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" width="11" height="11" aria-hidden="true">
    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.2" />
    <path d="M8 7v4M8 5.5v.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

export function PrayerWindowsPanel(props: {
  timings: PrayerTimings;
  timeFormat: '12h' | '24h';
  fardBuffers?: FardBuffers;
  madhab: Madhab;
  onMadhabChange: (m: Madhab) => void;
}) {
  const { locale, copy } = useLocale();
  const [open, setOpen] = useState(false);
  const isRTL = locale === 'ar' || locale === 'ur';

  const derived = useMemo(
    () => computeDerivedTimes(props.timings, props.fardBuffers ?? { fajr: 15, asr: 15 }),
    [props.timings, props.fardBuffers]
  );

  const data: PrayerWindow[] = PH_WINDOWS_BY_MADHAB[props.madhab] ?? PH_WINDOWS_BY_MADHAB.hanafi;
  const i18nLoc = locale !== 'en' ? PH_WINDOWS_I18N[locale] : undefined;
  const i18nMd = i18nLoc?.[props.madhab] ?? {};
  const ui = i18nLoc?._ui;

  const badgeHaram = ui?.badgeHaram ?? 'Haram';
  const badgeMakruhT = ui?.badgeMakruhT ?? 'Makruh tahrimi';
  const badgeMakruh = ui?.badgeMakruh ?? 'Makruh';
  const badgeOk = ui?.badgeOk ?? 'Permissible';
  const badgeInfo = ui?.badgeInfo ?? 'Info';

  return (
    <div className="ph-day-arc" style={{ marginTop: 14 }}>
      <div className="ph-arc-head">
        <div className="ph-arc-title">{copy.windowsToggle}</div>
        <div className="ph-arc-head-right">
          <button
            type="button"
            className="ph-windows-toggle"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            <span className="ph-windows-toggle-label">{open ? '−' : '+'}</span>
            <svg className="ph-windows-chevron" viewBox="0 0 16 16" fill="none" width="12" height="12" aria-hidden="true">
              <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>

      {open && (
        <div className="ph-windows-panel is-open">
          <div className="ph-windows-madhab-bar">
            <span className="ph-windows-madhab-label">{copy.schoolOfLaw}</span>
            <div className="ph-windows-madhab-btns" role="group" aria-label="Select madhab">
              {MADHABS.map((m) => {
                const lbl = locale === 'ar' ? PH_MADHAB_LABELS[m].ar : locale === 'ur' ? PH_MADHAB_LABELS[m].ur : PH_MADHAB_LABELS[m].name;
                return (
                  <button
                    key={m}
                    type="button"
                    className={`ph-wm-btn ${m === props.madhab ? 'is-active' : ''}`}
                    onClick={() => props.onMadhabChange(m)}
                  >
                    {lbl}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="ph-windows-panel-inner">
            {data.map((w) => {
              const t = i18nMd[w.key];
              const label = t?.label ?? w.label;
              const note = t?.note ?? w.note;
              const exception = t?.exception !== undefined ? t.exception : w.exception;
              const secLabel = locale === 'ar' ? w.label : w.ar;

              const span = resolveSpan(w.key, props.madhab, derived);
              let timeStr = '';
              if (w.strict === 'note' || span.from === null) {
                timeStr = '';
              } else if (w.strict === null) {
                timeStr = formatClock(span.from, props.timeFormat) + '+';
              } else if (span.to) {
                timeStr = formatRange(span.from, span.to, props.timeFormat);
              }

              let badgeCls: string, badgeText: string;
              if (w.strict === null) { badgeCls = 'is-ok'; badgeText = badgeOk; }
              else if (w.strict === true) { badgeCls = 'is-strict'; badgeText = badgeHaram; }
              else if (w.strict === false) { badgeCls = 'is-makruh'; badgeText = props.madhab === 'maliki' ? badgeMakruh : badgeMakruhT; }
              else { badgeCls = 'is-note'; badgeText = badgeInfo; }

              return (
                <div
                  key={w.key}
                  className={`ph-windows-row ${w.strict === 'note' ? 'is-note-row' : ''} ${isRTL ? 'is-rtl' : ''}`}
                  data-key={w.key}
                >
                  <div className="ph-windows-row-name">{label}</div>
                  {timeStr ? <div className="ph-windows-row-time">{timeStr}</div> : <div />}
                  <div className="ph-windows-row-ar">{secLabel}</div>
                  <div className="ph-windows-row-note">{note}</div>
                  <div style={{ paddingTop: 3, gridColumn: '1/-1' }}>
                    <span className={`ph-windows-badge ${badgeCls}`}>{badgeText}</span>
                  </div>
                  {exception ? (
                    <div className="ph-windows-row-exception">
                      <ExceptionIcon />
                      <span>{exception}</span>
                    </div>
                  ) : null}
                </div>
              );
            })}

            <div className="ph-windows-disclaimer">{copy.disclaimer}</div>
          </div>
        </div>
      )}
    </div>
  );
}

export { readWindowsMadhab };
